import { Button, ColorTile, SelectedElement, generateResolver, yup, VALIDATORS, SettingsFooter, STATUS_TYPES, MultipleToggableDropdown, Notification } from 'dyl-components'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Grid, Header, Portal, TextArea } from 'semantic-ui-react'
import TableDropdown from 'shared/TableDropdown'
import accountOptionsActions from 'actions/account_options';
import { MathUtils, StringUtils } from 'utils'

import "../index.scss"
import useWidthListener from 'shared/SettingsFooter/useWidthListener'
import useWindowWidth from 'shared/SettingsFooter/useWindowWidth'
import broadcastActions from 'actions/broadcast'
import productCategoryActions from 'actions/product_category'
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp'
import { useNavigate } from 'react-router-dom'
import AccountSearchResults from './AccountSearchResults'
import SelectionRender from './SelectionRender'

const LIMIT = 5;
const COLOR_MAPPING = {
    red: "#A02951",
    orange: "#c95a40",
    green: "#27A783"
};


const BannerDisplay = () => {
    const dispatch = useDispatch();
    const width = useWidthListener("settingsSidebar");
    const windowWidth = useWindowWidth();
    const { isReadingBroadcastMessage, broadcast_messages } = useSelector((state) => state.broadcast)
    const activeBroadcast = broadcast_messages?.find((broadcast) => broadcast.active)
    const { plansAndTiers, isReadingPlansAndTiers } = useSelector((state) => state.product_category)
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const navigate = useNavigate();

    const planAndTierOptions =
        !isReadingPlansAndTiers
            ? Object.entries(plansAndTiers || {})
                .filter(([key, value]) => Array.isArray(value))
                .map(([plan, tiers]) => ({
                    key: plan,
                    text: plan
                        .split(',')
                        .map((item) => item === "crm" ? "CRM" : StringUtils.capitalize(item.trim()))
                        .join(' + '),
                    value: plan,
                    hasOptions: tiers.length > 0,
                    options: tiers.map((tier) => ({
                        key: tier?.id,
                        text: StringUtils.capitalize(tier.name),
                        value: tier.id,
                        hasOptions: false,
                        parent: plan,
                        options: []
                    }))
                }))
            : [];

    const formatPlanDefaultValue = (planIds) => {
        if (!planIds || !planIds.length) return [];
        return planIds.map(id => {
            if (typeof id === 'object' && id !== null) return id;
            let name = String(id);
            if (!isReadingPlansAndTiers && plansAndTiers) {
                for (const [planName, tiers] of Object.entries(plansAndTiers)) {
                    if (Array.isArray(tiers)) {
                        const tier = tiers.find(t => t.id === id);
                        if (tier) {
                            const formattedPlanName = planName.split(',').map((item) => item === "crm" ? "CRM" : StringUtils.capitalize(item.trim())).join(' + ');
                            name = `${formattedPlanName}/${StringUtils.capitalize(tier.name)}`;
                            break;
                        }
                    }
                }
            }
            return { value: id, name };
        });
    };
    const {
        control,
        setValue,
        reset,
        formState: { isValid, isDirty },
        handleSubmit,
        watch
    } = useForm({
        mode: 'onChange',

        defaultValues: {
            message: activeBroadcast?.message || "",
            message_type: activeBroadcast?.message_type ? activeBroadcast.message_type.toLowerCase().replace(/ /g, '_') : "",
            permission_type: activeBroadcast?.partial_alerts?.permission_type || [],
            banner_bg_color: activeBroadcast?.color || "",
            link: activeBroadcast?.link || "",
            accounts: activeBroadcast?.partial_alerts?.customer_id || [],
            billing_schedule: activeBroadcast?.partial_alerts?.billing_schedule || [],
            plan: formatPlanDefaultValue(activeBroadcast?.partial_alerts?.plan),
            customer_type: activeBroadcast?.partial_alerts?.customer_type || []
        },

        resolver: generateResolver({
            message: yup
                .string()
                .max(160, 'Message must be at most 160 characters')
                .required('Message is required'),

            message_type: yup
                .string()
                .required('Message Type is required'),

            permission_type: yup
                .array()
                .min(1, 'Permission Type is required'),

            banner_bg_color: yup
                .string()
                .required('Banner Background Color is required'),

            link: VALIDATORS.WEBSITE().nullable(true),
        }),
    });
    const onSearchAction = async (search) => {
        if (search?.trim()) {
            try {
                const { data: account_data, count } = await dispatch(accountOptionsActions.read({ page: 1, search: search, limit: LIMIT, customer: true }));
                return { ...account_data, data: account_data, count, open: true, page: 1, search: search };
            } catch (error) {
                console.log('error: ', error);

            }
        }
    }
    const onCreateBroadcast = async (data) => {
        const payload = {
            color: data.banner_bg_color,
            message: data.message,
            link: data.link || "",
            message_type: data.message_type
                ? data.message_type
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                : "",

            partial_alerts: {
                billing_schedule: data.billing_schedule || [],
                customer_id: (data.accounts || []).map(
                    account => account.id
                ),
                customer_type: data.customer_type || [],
                permission_type: data.permission_type || [],
                plan: (data.plan || []).map(p => typeof p === 'object' && p !== null ? p.value : p)
            }
        };

        try {
            await dispatch(
                broadcastActions.createBroadcast(payload)
            );
            Notification.alert(
                `Successfully created broadcast message !`,
                STATUS_TYPES.SUCCESS,
                true
            );
            await dispatch(broadcastActions.getBroadcastMessages());
        } catch (error) {
            Notification.alert(
                `Failed to create broadcast message !`,
                STATUS_TYPES.ERROR,
                true
            );
        }
    };
    const handleExpireBroadcast = async () => {
        try {
            await dispatch(broadcastActions.inactivateBroadcast());
            Notification.alert(
                `Successfully expired broadcast message!`,
                STATUS_TYPES.SUCCESS,
                true
            );
            reset({
                message: "",
                message_type: "",
                permission_type: [],
                banner_bg_color: "",
                link: "",
                accounts: [],
                billing_schedule: [],
                plan: [],
                customer_type: []
            })
            await dispatch(broadcastActions.getBroadcastMessages());
        } catch (error) {
            Notification.alert(
                `Failed to expire broadcast message!`,
                STATUS_TYPES.ERROR,
                true
            );
        }
    }

    useEffect(() => {
        dispatch(productCategoryActions.readProductPlansAndTiers())
        dispatch(broadcastActions.getBroadcastMessages())
    }, [dispatch])

    useEffect(() => {
        if (activeBroadcast) {
            reset({
                message: activeBroadcast.message || "",
                message_type: activeBroadcast.message_type ? activeBroadcast.message_type.toLowerCase().replace(/ /g, '_') : "",
                permission_type: activeBroadcast.partial_alerts?.permission_type || [],
                banner_bg_color: activeBroadcast.color || "",
                link: activeBroadcast.link || "",
                accounts: activeBroadcast.partial_alerts?.customer_id || [],
                billing_schedule: activeBroadcast.partial_alerts?.billing_schedule || [],
                plan: formatPlanDefaultValue(activeBroadcast.partial_alerts?.plan),
                customer_type: activeBroadcast.partial_alerts?.customer_type || []
            })
        }
        // eslint-disable-next-line
    }, [activeBroadcast, reset, plansAndTiers, isReadingPlansAndTiers])
    return (
        <div className="BannerDisplay">
            <Header color="primary" disabled={activeBroadcast?.active}>Banner Display</Header>
            <Form loading={isReadingBroadcastMessage} >
                <Grid>
                    <Grid.Column width={5}>
                        <Form.Field required disabled={activeBroadcast?.active}>
                            <label>Message</label>
                            <Controller
                                name='message'
                                control={control}
                                render={({ field: { onChange, onFocus, value, name }, fieldState: { error } }) => {
                                    return (
                                        <>
                                            <TextArea
                                                name={name}
                                                value={value}
                                                placeholder='Type Message'
                                                onInput={onChange}
                                                onFocus={onFocus}
                                                label='Message'
                                                required
                                                style={{ marginBottom: '10px' }}
                                                error={error?.message}
                                                rows={4}
                                                maxLength={160}
                                            />
                                            <div className="TextingPreferences_Info">
                                                Character Count: {value?.length || 0}/{160}
                                            </div>
                                        </>
                                    )
                                }}
                            />
                            <Controller
                                name='link'
                                control={control}
                                render={({ field: { onChange, onFocus, value, name }, fieldState: { error } }) => {
                                    return (
                                        <Form.Input
                                            name={name}
                                            value={value}
                                            placeholder='Insert Link'
                                            onInput={onChange}
                                            onFocus={onFocus}
                                            label='Link'
                                            error={error?.message}
                                        />
                                    )
                                }}
                            />
                        </Form.Field>
                    </Grid.Column>
                    <Grid.Column width={5}>
                        <Controller
                            name='message_type'
                            control={control}
                            render={({ field: { onChange, onFocus, value, name } }) => {
                                return (
                                    <Form.Select
                                        label="Message Type"
                                        options={[
                                            { text: 'Maintenance', value: 'maintenance' },
                                            { text: 'Service Interruption', value: 'service_interruption' },
                                            { text: 'Announcement', value: 'announcement' },
                                            { text: 'Warning', value: 'warning' },
                                            { text: 'Other', value: 'other' },
                                        ]}
                                        disabled={activeBroadcast?.active}
                                        value={value}
                                        onChange={(_, { value }) => {
                                            onChange(value);
                                            if (value === "service_interruption") {
                                                setValue("banner_bg_color", "red", { shouldValidate: true })
                                            }
                                            else if (value === "maintenance" || value === "warning") {
                                                setValue("banner_bg_color", "orange", { shouldValidate: true })
                                            }
                                            else if (value === "announcement") {
                                                setValue("banner_bg_color", "green", { shouldValidate: true })
                                            }
                                            else {
                                                setValue("banner_bg_color", "", { shouldValidate: true })
                                            }
                                        }}
                                        onFocus={onFocus}
                                        name={name}
                                        required
                                        placeholder='Select Message Type'
                                    />
                                )
                            }}
                        />
                        <Controller
                            name="permission_type"
                            control={control}
                            render={({ field: { onChange, onFocus, value, name } }) => (
                                <Form.Select
                                    label="Permission Type"
                                    options={[
                                        { text: "Owner", value: "Owner" },
                                        { text: "Administrator", value: "Administrator" },
                                        { text: "User", value: "User" },
                                        { text: "Custom", value: "Custom" },
                                    ]}
                                    value={value}
                                    onChange={(_, { value: selectedPermissions }) => {
                                        const permissions = new Set(selectedPermissions);

                                        if (
                                            permissions.has("Administrator") ||
                                            permissions.has("User") ||
                                            permissions.has("Custom")
                                        ) {
                                            permissions.add("Owner");
                                        }

                                        if (
                                            permissions.has("User") ||
                                            permissions.has("Custom")
                                        ) {
                                            permissions.add("Administrator");
                                        }

                                        onChange(
                                            ["Owner", "Administrator", "User", "Custom"].filter(
                                                permission => permissions.has(permission)
                                            )
                                        );
                                    }}
                                    disabled={activeBroadcast?.active}
                                    onFocus={onFocus}
                                    name={name}
                                    required
                                    multiple
                                    placeholder="Select Permission Type(s)"
                                />
                            )}
                        />

                    </Grid.Column>
                    <Grid.Column width={5}>
                        <Form.Field required disabled={activeBroadcast?.active}>
                            <label>Banner Background Color</label>
                            <Controller
                                name='banner_bg_color'
                                control={control}
                                render={({ field: { onChange, name, value } }) => (
                                    <React.Fragment>
                                        <div className='BannerDisplay__colorTiles'>
                                            {Object.entries(COLOR_MAPPING).map(([colorName, hexColor]) => {
                                                const selected = value === colorName;
                                                return (
                                                    <SelectedElement key={colorName} selected={selected} onClick={() => { watch("message_type") === "other" && onChange(colorName) }}>
                                                        <ColorTile color={hexColor} style={{ opacity: selected ? 1 : 0.5 }} />
                                                    </SelectedElement>
                                                )
                                            })}
                                        </div>
                                    </React.Fragment>
                                )}
                            />
                        </Form.Field>
                    </Grid.Column>
                    <Grid.Column width={1}>
                    </Grid.Column>
                </Grid>
                <Header color="primary" disabled={activeBroadcast?.active} >Partial Alerts</Header>

                <Form.Field width={8} disabled={activeBroadcast?.active}>
                    <Controller
                        name="accounts"
                        control={control}
                        render={({
                            field: { name, value, onChange },
                            fieldState: { error },
                        }) => (
                            <Form.Field
                                control={TableDropdown}
                                label="Accounts"
                                value={value}
                                onChange={(e, newValue) => {
                                    e.stopPropagation()
                                    onChange(newValue);
                                }}
                                isSelectionVisible={value?.length > 0}
                                SelectionRender={SelectionRender}
                                SearchResultsRender={AccountSearchResults}
                                onSearchAction={onSearchAction}
                                searchPlaceholder="Search by account name or ID"
                                errorMessage={error && error.message}
                                isSeperateResults
                            />
                        )}
                    />
                </Form.Field>
                <Form.Group>
                    <Form.Field width={4} disabled={activeBroadcast?.active}>
                        <Controller
                            name='billing_schedule'
                            control={control}
                            render={({ field: { onChange, onFocus, value, name } }) => {
                                return (
                                    <Form.Select
                                        label="Billing Schedule"
                                        upward
                                        options={[
                                            { text: 'Monthly', value: 'monthly' },
                                            { text: 'Quarterly', value: 'quarterly' },
                                            { text: 'Semi-Annually', value: 'bi-annually' },
                                            { text: 'Annually', value: 'annually' },
                                        ]}
                                        multiple
                                        value={value}
                                        onChange={(_, { value }) => {
                                            onChange(value);
                                        }}
                                        onFocus={onFocus}
                                        name={name}
                                        placeholder='Select Billing Schedule(s)'
                                    />
                                )
                            }}
                        />
                    </Form.Field>
                    <Form.Field width={4} disabled={activeBroadcast?.active}>
                        <Controller
                            name='plan'
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <Form.Field
                                        control={MultipleToggableDropdown}
                                        values={value || []}
                                        nested_options={planAndTierOptions}
                                        onChange={(_, { value }) => {
                                            onChange(value);
                                        }}
                                        placeholder="Select Plan/Tier(s)"
                                        label="Plan/Tier"
                                        showSearchIcon={false}
                                        isExpandable
                                        className="BannerDisplay__MultipleToggableDropdown"
                                        topBorder={false}
                                        isOnlyAllowChildSelection={true}
                                        height={40}
                                        upward
                                    />

                                )
                            }}
                        />
                    </Form.Field>
                    <Form.Field width={4} disabled={activeBroadcast?.active}>
                        <Controller
                            name='customer_type'
                            control={control}
                            render={({ field: { onChange, onFocus, value, name } }) => {
                                return (
                                    <Form.Select
                                        label="Customer Type"
                                        upward
                                        options={[
                                            { text: 'Funded', value: 'Funded' },
                                            { text: 'Trial', value: 'Trial' },
                                            { text: 'Demo', value: 'Demo' },
                                        ]}
                                        value={value}
                                        onChange={(_, { value }) => {
                                            onChange(value);
                                        }}
                                        onFocus={onFocus}
                                        name={name}
                                        multiple
                                        placeholder='Select Customer Type(s)'
                                    />
                                )
                            }}
                        />
                    </Form.Field>
                </Form.Group>
            </Form>
            <Portal open>
                <SettingsFooter
                    style={{ width: MathUtils.calculatePercentage(windowWidth, windowWidth - width) }}
                    className={`Webform__menu`}
                    rightOptions={[
                        <Button onClick={() => !activeBroadcast && isDirty ? setIsConfirmationOpen(true) : navigate('/settings/dyl-admin')} status={'cancel'} basic>Cancel</Button>,
                        <Button disabled={!isValid || isReadingBroadcastMessage} loading={isReadingBroadcastMessage} onClick={activeBroadcast ? handleExpireBroadcast : handleSubmit(onCreateBroadcast)} primary>
                          {activeBroadcast ? 'Expire' : 'Broadcast'}
                        </Button>
                    ]}
                />
            </Portal>
            <FullScreenModalPopUp
                header={"Changes not saved"}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    setIsConfirmationOpen(false);
                    navigate('/settings/dyl-admin')
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </div>
    )
}

export default BannerDisplay